import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getCourseTagsApi, addCourseTagApi, deleteCourseTagApi, updateCourseTagApi } from 'Services/allApi'; // Ensure you have the update function
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdDelete, MdEdit } from "react-icons/md";
import Swal from 'sweetalert2';

const CourseTags = () => {
    const { id } = useParams();
    const [tagsData, setTagsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [newTag, setNewTag] = useState({ key: '', value: '', colour: '#000000' });
    const [editTag, setEditTag] = useState({ id: null, key: '', value: '', colour: '#000000' });
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const fetchTagsData = async () => {
            try {
                const response = await getCourseTagsApi(id);
                if (response.error) {
                    console.error("Error fetching course tags data:", response.message);
                    toast.error("Error fetching course tags data");
                } else {
                    setTagsData(response.data || []);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Error fetching data");
            } finally {
                setLoading(false);
            }
        };

        fetchTagsData();
    }, [id]);

    const handleNewTagChange = (e) => {
        const { name, value } = e.target;
        setNewTag((prev) => ({ ...prev, [name]: value }));
        setErrorMessage('');
    };

    const handleEditTagChange = (e) => {
        const { name, value } = e.target;
        setEditTag((prev) => ({ ...prev, [name]: value }));
        setErrorMessage('');
    };

    const handleAddTag = async () => {
        const { key, value, colour } = newTag;

        if (!key || !value || !colour) {
            setErrorMessage('All fields are required');
            toast.error('All fields are required');
            return;
        }

        try {
            const response = await addCourseTagApi({ key, value, colour });
            if (response.error) {
                console.error("Error adding course tag:", response.message);
                toast.error("Error adding course tag");
            } else {
                setTagsData([...tagsData, response.data]);
                setShowAddModal(false);
                setNewTag({ key: '', value: '', colour: '#000000' });
                toast.success("Tag added successfully");
            }
        } catch (error) {
            console.error("Error creating course tag:", error);
            toast.error("Error creating course tag");
        }
    };

    const handleDeleteTag = async (tagId) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "Do you want to delete this tag?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
            customClass: {
                popup: 'dark:bg-gray-800 dark:text-white',
                title: 'dark:text-white',
                confirmButton: 'dark:bg-blue-500 dark:hover:bg-blue-700 dark:text-white',
                cancelButton: 'dark:bg-red-500 dark:hover:bg-red-700 dark:text-white'
            }
        });

        if (result.isConfirmed) {
            try {
                const response = await deleteCourseTagApi(tagId);
                if (response.error) {
                    console.error("Error deleting course tag:", response.message);
                    toast.error("Error deleting course tag");
                } else {
                    setTagsData(tagsData.filter((tag) => tag.id !== tagId));
                    toast.success("Course tag deleted successfully");
                }
            } catch (error) {
                console.error("Error deleting course tag:", error);
                toast.error("Error deleting course tag");
            }
        }
    };

    const handleEditTag = async () => {
        const { key, value, colour } = editTag;

        if (!key || !value || !colour) {
            setErrorMessage('All fields are required');
            toast.error('All fields are required');
            return;
        }

        try {
            const response = await updateCourseTagApi(editTag.id, { key, value, colour });
            if (response.error) {
                console.error("Error updating course tag:", response.message);
                toast.error("Error updating course tag");
            } else {
                setTagsData(tagsData.map((tag) => (tag.id === editTag.id ? response.data : tag)));
                setShowEditModal(false);
                setEditTag({ id: null, key: '', value: '', colour: '#000000' });
                toast.success("Tag updated successfully");
            }
        } catch (error) {
            console.error("Error updating course tag:", error);
            toast.error("Error updating course tag");
        }
    };

    return (
        <div className="w-full max-w-6xl mx-auto p-8 bg-white shadow-lg rounded-lg dark:!bg-navy-700 dark:text-white">
           
            <h2 className="text-4xl font-semibold mb-6">Course Tags</h2>

            <div className="mb-6 flex justify-end">
                <button
                    onClick={() => setShowAddModal(true)}
                    className="bg-green-600 text-white px-4 py-2 rounded-md"
                >
                    Add Tag
                </button>
            </div>

            {loading && (
                <div className="text-center">
                    <p className="text-lg text-purple-800">Loading...</p>
                </div>
            )}

            {!loading && tagsData.length === 0 && (
                <div className="text-center">
                    <p className="text-lg text-purple-800">No course tags to display</p>
                </div>
            )}

            {!loading && tagsData.length > 0 && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-6">
                    {tagsData.map((tag) => (
                        <div key={tag.id} className="mb-6 p-6 border border-gray-200 rounded-lg shadow-md dark:border-gray-600 dark:bg-[#0b14374d]">
                            <h3 className="text-xl font-bold mb-4">{tag.key}</h3>
                            <p className="w-full px-4 py-2 border rounded-lg" style={{ backgroundColor: tag.colour, color: '#fff' }}>
                                {tag.value}
                            </p>
                            <div className="flex justify-end space-x-4 mt-4">
                                <button
                                    onClick={() => {
                                        setEditTag(tag);
                                        setShowEditModal(true);
                                    }}
                                    className="rounded-md border-2 border-yellow-600 px-3 py-2 font-semibold text-yellow-600 shadow-sm hover:bg-yellow-100 flex items-center"
                                >
                                    <MdEdit className="mr-2" /> Edit
                                </button>
                                <button
                                    onClick={() => handleDeleteTag(tag.id)}
                                    className="rounded-md border-2 border-red-600 px-3 py-2 font-semibold text-red-600 shadow-sm hover:bg-red-100 flex items-center"
                                >
                                    <MdDelete className="mr-2" /> Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            
            {showAddModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded-xl w-1/2 dark:bg-gray-800 dark:text-white">
                        <h3 className="text-2xl font-bold mb-4">Add Tag</h3>
                        <input
                            type="text"
                            name="key"
                            placeholder="Enter tag key"
                            value={newTag.key}
                            onChange={handleNewTagChange}
                            className="w-full px-4 py-2 border rounded-lg mb-4 dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white"
                        />
                        <input
                            type="text"
                            name="value"
                            placeholder="Enter tag value"
                            value={newTag.value}
                            onChange={handleNewTagChange}
                            className="w-full px-4 py-2 border rounded-lg mb-4 dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white"
                        />
                        <input
                            type="color"
                            name="colour"
                            value={newTag.colour}
                            onChange={handleNewTagChange}
                            className="w-full mb-4"
                        />
                        {errorMessage && (
                            <p className="text-red-600 text-sm m-2">{errorMessage}</p>
                        )}
                        <div className="flex justify-between">
                            <button
                                onClick={() => {
                                    setShowAddModal(false);
                                    setErrorMessage('');
                                }}
                                className="bg-gray-500 text-white px-4 py-2 rounded-md"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddTag}
                                className="bg-green-600 text-white px-4 py-2 rounded-md"
                            >
                                Add Tag
                            </button>
                        </div>
                    </div>
                </div>
            )}

        
            {showEditModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded-xl w-1/2 dark:bg-gray-800 dark:text-white">
                        <h3 className="text-2xl font-bold mb-4">Edit Tag</h3>
                        <input
                            type="text"
                            name="key"
                            placeholder="Enter tag key"
                            value={editTag.key}
                            onChange={handleEditTagChange}
                            className="w-full px-4 py-2 border rounded-lg mb-4 dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white"
                        />
                        <input
                            type="text"
                            name="value"
                            placeholder="Enter tag value"
                            value={editTag.value}
                            onChange={handleEditTagChange}
                            className="w-full px-4 py-2 border rounded-lg mb-4 dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white"
                        />
                        <input
                            type="color"
                            name="colour"
                            value={editTag.colour}
                            onChange={handleEditTagChange}
                            className="w-full mb-4"
                        />
                        {errorMessage && (
                            <p className="text-red-600 text-sm m-2">{errorMessage}</p>
                        )}
                        <div className="flex justify-between">
                            <button
                                onClick={() => {
                                    setShowEditModal(false);
                                    setErrorMessage('');
                                }}
                                className="bg-gray-500 text-white px-4 py-2 rounded-md"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleEditTag}
                                className="bg-purple-600 text-white px-4 py-2 rounded-md"
                            >
                                Update Tag
                            </button>
                        </div>
                    </div>
                </div>
            )}
             <ToastContainer  position="top-center" autoClose={5000} hideProgressBar={false} newestOnTop closeButton={true}/>
        </div>
    );
};

export default CourseTags;